import { Hex, Latin1, MD5 as CryptoMD5 } from "crypto-es";
import { Service } from "rainbow-web-sdk/services/service";
import { v4 as uuidv4 } from 'uuid';

/** @internal */
export const UTILITIES_SVC = 'UtilitiesService';

export class UtilitiesService extends Service {
    /**
     * @internal
     * */
    public static getInstance() {
        return Service.create(UTILITIES_SVC, UtilitiesService);
    }

    /**
     * @internal
     * */
    public static build() {
        return new UtilitiesService();
    }

    private rtlDirCheck: RegExp;
    private usableRandomChars: string;

    private constructor() {
        super();
        const rtlChars = '\u0591-\u07FF\u200F\u202B\u202E\uFB1D-\uFDFD\uFE70-\uFEFC';
        this.rtlDirCheck = new RegExp('^[^' + rtlChars + ']*?[' + rtlChars + ']');
        this.usableRandomChars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    }

    /**
     * @internal
     * */
    public async asyncForEach<T>(array: T[], callback: (item: T, index: number, array: T[]) => Promise<void>): Promise<void> {
        for (let i = 0; i < array.length; i++) {
            await callback(array[i], i, array);
        }
    }

    /**
     * @internal
     * */
    public isRTL(str: string): boolean {
        return this.rtlDirCheck.test(str.charAt(0));
    }

    /**
     * @internal
     * */
    public randomString(length: number = 10): string {
        let outString: string = "";
        let rnd: number;
        while (length > 0) {
            rnd = Math.floor(Math.random() * this.usableRandomChars.length);
            outString += this.usableRandomChars.charAt(rnd);
            length--;
        }
        return outString;
    }

    /**
     * @internal
     * */
    public getUUID(): string {
        return uuidv4();
    }

    /**
     * @internal
     * */
    public addUrlParameter(url: string, urlParameter) {
        if (!url.includes("?")) url += "?";
        else url += "&";
        url += urlParameter;

        return url;
    }

    /**
     * @internal
     * */
    public isNumeric(str: any): boolean {
        if (typeof str !== "string") return false;

        return !isNaN(parseFloat(str));
    }

    /**
     * @internal
     * */
    public asyncTimeout(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * @internal
     * */
    public selectRandomly(possibilities: any[]) {
        const random = Math.floor(Math.random() * possibilities.length);
        return possibilities[random];
    }

    /**
     * @internal
     * */
    public arrayMove(arr: any[], oldIndex: number, newIndex: number) {
        while (oldIndex < 0) {
            oldIndex += arr.length;
        }
        while (newIndex < 0) {
            newIndex += arr.length;
        }
        if (newIndex >= arr.length) {
            let k = newIndex - arr.length + 1;
            while (k--) {
                arr.push(undefined);
            }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    }

    /**
     * @internal
     * */
    public removeSpecialChars(initialStr: string): string {
        return initialStr.replace(/[^a-zA-Z0-9 ]/g, '');
    }

    public async getBase64Image(url: string): Promise<string> {
        return new Promise((resolve) => {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            const canvas = document.createElement('canvas');
            image.onload = () => {
                canvas.width = image.width;
                canvas.height = image.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(image, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
            image.src = url;
        });
    }

    /**
     * Method checks for validity of an API Url for security reasons
     * @param environmentApiUrl - url to check
     * @returns true if environment is valid, false otherwise
     */
    public async checkEnvironmentApiUrl(environmentApiUrl: string): Promise<boolean> {
        if (!environmentApiUrl) return false;
        try {

            const apiUrlToTest = environmentApiUrl.indexOf('https://') < 0 ? `https://${environmentApiUrl}` : environmentApiUrl;

            const url = `${apiUrlToTest}/api/rainbow/admin/v1.0/about`;
            const headers = { Accept: 'application/json' };
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) return false;
            return true;
        }
        catch {
            return false;
        }
    }
}

export const asyncForEach = async (array: any[], callback: any) => {
    for (let i = 0; i < array.length; i++) {
        await callback(array[i], i, array);
    }
};

export const roundTo = (value: number, decimals: number = 2): number => {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
};

/**
 * Get the unique YouTube video ID from its URL.
 * The ID of a YouTube video is used to uniquely identify each video on the platform.
 * This ID allows you to find the video precisely in the YouTube database and access it directly via its unique URL.
 */
export const getYoutubeVideoIdFromURL = (url: string): string => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[7].length > 10) { return match[7]; }
    return null;
};

/**
 * Computes the MD5 hash of a given value encoded in Latin-1.
 * The input is first parsed using Latin-1 encoding before hashing,
 * and the result is returned as a hexadecimal string.
 * @param date - The value to hash (typically a string or date-like value)
 * @returns The MD5 hash as a lowercase hexadecimal string
 */
export const MD5 = (date: any): string => { return CryptoMD5(Latin1.parse(date)).toString(Hex); };
